import React from "react";
import { useRillContext } from "../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../utils/get-avatar-url";
import CreateBlockTimeModal, {
  BLOCK_TIME_SCHEDULE_CLASSES,
  BLOCK_TIME_SCROLL_SELECT_DATE,
  submitBlockTimePayloads,
} from "./CreateBlockTimeModal";
import CreateClassModal from "./CreateClassModal";
import { fetchSchedulingBookable } from "./scheduling-bookable-fetch";
import {
  consumeSchedulingCreateIntent,
  consumeSchedulingEditIntent,
} from "./scheduling-create-from-overview";
import { buildDuplicateClassPayload } from "./scheduling-duplicate-payloads";
import { canManageSchedulingRecord } from "./scheduling-event-ownership";

function extractCoachList(rawCoaches, theme) {
  if (!Array.isArray(rawCoaches)) return [];
  const seen = new Set();
  const out = [];
  for (const entry of rawCoaches) {
    const c = entry?.coachId ?? entry?.coach ?? entry;
    if (!c || typeof c !== "object") continue;
    const id = String(c._id ?? c.id ?? "");
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      name: String(c.name ?? "").trim(),
      avatarUrl: getAvatarUrl(c, false, theme, theme !== "dark"),
    });
  }
  return out;
}

import { formatDailyWeekdays } from "./recurring-weekday-encoding";
import SchedulingEmptyState from "./SchedulingEmptyState";
import { CardListSkeleton } from "./SchedulingSkeletons";
import { buildSchedulingCalendarPreviewUrl } from "./scheduling-preview-url";

const RECURRENCE_LABELS = {
  one_time: "One-time",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

function formatDuration(minutes) {
  if (minutes == null) return "—";
  if (minutes < 60) return `${minutes} min${minutes !== 1 ? "s" : ""}`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function formatTime(str) {
  if (!str || typeof str !== "string") return "—";
  const [h, m] = str.split(":").map(Number);
  if (Number.isNaN(h)) return str;
  const period = h >= 12 ? "pm" : "am";
  const hour = h % 12 || 12;
  const mins = m ? `:${String(m).padStart(2, "0")}` : "";
  return `${hour}${mins}${period}`;
}

function localDateFromField(d) {
  if (!d) return null;
  if (typeof d === "string") {
    const m = d.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m)
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
  }
  return d instanceof Date ? d : null;
}

function formatDateRange(start, end) {
  if (!start && !end) return "—";
  const toShort = (d) => {
    if (!d) return "";
    const date = localDateFromField(d);
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  if (!end || start === end) return toShort(start);
  return `${toShort(start)} – ${toShort(end)}`;
}

/** Map API class document to card props */
function classToCard(c, theme) {
  const recurrenceBase =
    RECURRENCE_LABELS[c.recurrence_type] ?? c.recurrence_type ?? "—";
  const recurrence =
    c.recurrence_type === "daily"
      ? `${recurrenceBase} ${formatDailyWeekdays(c.recurrence_weekdays)}`.trim()
      : recurrenceBase;
  const duration = formatDuration(c.duration_minutes);
  const capacity =
    c.capacity != null
      ? `${c.capacity} ${c.capacity === 1 ? "Seat" : "Seats"}`
      : "—";
  const classCoaches = extractCoachList(c.coaches, theme);
  return {
    id: c._id,
    raw: c,
    title: c.name ?? "",
    description: c.description ?? "",
    days: formatDateRange(c.recurrence_start_date, c.recurrence_end_date),
    time: formatTime(c.start_time),
    duration,
    recurrence,
    location: c.location ?? "—",
    capacity,
    thumbnail: c.thumbnail_url || "/img/new-icons/dumbbell.svg",
    thumbnailIsPhoto: !!c.thumbnail_url,
    coaches: classCoaches,
    coachesExtra: classCoaches.length > 3 ? classCoaches.length - 3 : null,
    hasHistory: false,
    status: c.status,
    isExpired: c.isExpired,
  };
}

const SchedulingClasses = ({
  hasNonPullScheduling = true,
  hasPressScheduling = false,
  hasPullScheduling = false,
  canPushUser = true,
  canPullUser = true,
}) => {
  const showPermissionsAction = (event) =>
    hasNonPullScheduling &&
    (hasPressScheduling || hasPullScheduling || Boolean(event?.is_restricted));
  const { locals } = useRillContext();
  const activeUser = locals.activeUser;
  const theme = activeUser.webAppTheme || "dark";
  const canManageClass = (cls) =>
    canManageSchedulingRecord(cls?.raw, activeUser);
  const [activeFilter, setActiveFilter] = React.useState("active");
  const [createClassModalOpen, setCreateClassModalOpen] = React.useState(false);
  const [editingClass, setEditingClass] = React.useState(null);
  const [modalInitialScrollSection, setModalInitialScrollSection] =
    React.useState(null);
  const [blockTimeModalOpen, setBlockTimeModalOpen] = React.useState(false);
  const [blockTimePreset, setBlockTimePreset] = React.useState(null);
  const [menuOpenId, setMenuOpenId] = React.useState(null);
  const [classes, setClasses] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [filterQuery, setFilterQuery] = React.useState("");
  const [filterRecurrence, setFilterRecurrence] = React.useState("");
  const [filterDateFrom, setFilterDateFrom] = React.useState("");
  const [filterDateTo, setFilterDateTo] = React.useState("");
  const [filterDurationMin, setFilterDurationMin] = React.useState("");
  const [filterDurationMax, setFilterDurationMax] = React.useState("");
  const filterPanelRef = React.useRef(null);

  const activeFilterCount =
    (filterQuery.trim() ? 1 : 0) +
    (filterRecurrence ? 1 : 0) +
    (filterDateFrom ? 1 : 0) +
    (filterDateTo ? 1 : 0) +
    (filterDurationMin ? 1 : 0) +
    (filterDurationMax ? 1 : 0);

  const clearFilters = () => {
    setFilterQuery("");
    setFilterRecurrence("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterDurationMin("");
    setFilterDurationMax("");
  };

  React.useEffect(() => {
    if (!filterOpen) return;
    const handler = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen]);

  const fetchClasses = React.useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const res = await fetch("/actions/scheduling/classes", {
        method: "GET",
        credentials: "same-origin",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          data?.error ??
          data?.message ??
          (typeof data === "string" ? data : "Failed to load classes");
        throw new Error(msg);
      }
      const list = Array.isArray(data) ? data : [];
      setClasses(list.map((c) => classToCard(c, theme)));
    } catch (e) {
      setLoadError(e?.message ?? "Failed to load classes");
      setClasses([]);
    } finally {
      setLoading(false);
    }
  }, [theme]);

  React.useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  React.useEffect(() => {
    if (consumeSchedulingCreateIntent("class")) {
      setEditingClass(null);
      setModalInitialScrollSection(null);
      setCreateClassModalOpen(true);
    }
  }, []);

  const pendingEditIdRef = React.useRef(consumeSchedulingEditIntent("class"));
  React.useEffect(() => {
    const id = pendingEditIdRef.current;
    if (!id || classes.length === 0) return;
    const match = classes.find(
      (c) => String(c.raw?._id ?? c.id) === String(id)
    );
    if (match) {
      pendingEditIdRef.current = null;
      if (!canManageSchedulingRecord(match.raw, activeUser)) return;
      setModalInitialScrollSection(null);
      setEditingClass(match.raw);
      setCreateClassModalOpen(true);
    }
  }, [classes, activeUser]);

  const deleteClass = React.useCallback(
    async (cls) => {
      const id = cls.raw?._id ?? cls.id;
      if (!id) return;
      if (!window.confirm("Delete this class? This cannot be undone.")) {
        return;
      }
      try {
        const res = await fetch(
          `/actions/scheduling/classes/${encodeURIComponent(String(id))}`,
          { method: "DELETE", credentials: "same-origin" }
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            data.error ?? data.message ?? "Failed to delete class"
          );
        }
        setMenuOpenId(null);
        fetchClasses();
      } catch (e) {
        window.alert(e?.message ?? "Failed to delete class");
      }
    },
    [fetchClasses]
  );

  const duplicateClass = React.useCallback(
    async (cls) => {
      if (!cls?.raw) return;
      setMenuOpenId(null);
      try {
        const payload = buildDuplicateClassPayload(cls.raw);
        const res = await fetch("/actions/scheduling/classes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            data.error ?? data.message ?? "Failed to duplicate class"
          );
        }
        fetchClasses();
      } catch (e) {
        window.alert(e?.message ?? "Failed to duplicate class");
      }
    },
    [fetchClasses]
  );

  return (
    <div className="scheduling-classes">
      <header className="scheduling-classes-header">
        <hgroup className="scheduling-classes-title-group">
          <h1 className="scheduling-classes-title">Classes</h1>
          <p className="scheduling-classes-subtitle">
            Run your classes, schedules, and coaching operations seamlessly.
          </p>
          <div className="scheduling-classes-header-buttons">
            <button
              type="button"
              data-button-type="primary"
              className="scheduling-classes-create-btn"
              onClick={() => {
                setEditingClass(null);
                setModalInitialScrollSection(null);
                setCreateClassModalOpen(true);
              }}
            >
              + Create Class
            </button>
            <button
              type="button"
              data-button-type="outline"
              className="scheduling-classes-block-time-btn"
              onClick={() => {
                setBlockTimePreset({
                  scheduleKind: BLOCK_TIME_SCHEDULE_CLASSES,
                });
                setBlockTimeModalOpen(true);
              }}
            >
              <img
                src="/img/new-icons/slash.svg"
                alt=""
                className="scheduling-classes-block-icon"
              />
              Block Time : Classes
              <span className="scheduling-classes-info-icon">
                <img
                  src="/img/new-icons/vuesax-linear-tick-circle-schedule.svg"
                  alt="Info"
                />
                <span
                  className="scheduling-classes-info-tooltip"
                  role="tooltip"
                >
                  Block time for all the classes, This will not block time for
                  PT Sessions and facilities
                </span>
              </span>
            </button>
          </div>
        </hgroup>
      </header>

      <div className="scheduling-classes-box">
        <nav className="scheduling-classes-tabs">
          <button
            type="button"
            className="scheduling-classes-tab"
            aria-current={activeFilter === "active" ? "page" : undefined}
            onClick={() => setActiveFilter("active")}
          >
            Active
          </button>
          <button
            type="button"
            className="scheduling-classes-tab"
            aria-current={activeFilter === "expired" ? "page" : undefined}
            onClick={() => setActiveFilter("expired")}
          >
            Expired
          </button>
          <div className="scheduling-classes-filter-wrap" ref={filterPanelRef}>
            <button
              type="button"
              className="scheduling-classes-filter-btn"
              aria-expanded={filterOpen}
              onClick={() => setFilterOpen((o) => !o)}
            >
              <img src="/img/new-icons/filter-white.svg" alt="" />
              Filter
              {activeFilterCount > 0 && (
                <span className="scheduling-classes-filter-badge">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {filterOpen && (
              <div className="scheduling-classes-filter-panel" role="dialog">
                <label className="scheduling-classes-filter-label">
                  Filter by name
                  <input
                    type="text"
                    className="scheduling-classes-filter-input"
                    placeholder="Search classes…"
                    value={filterQuery}
                    onChange={(e) => setFilterQuery(e.target.value)}
                    autoFocus
                  />
                </label>
                <label className="scheduling-classes-filter-label">
                  Recurrence
                  <select
                    className="scheduling-classes-filter-input"
                    value={filterRecurrence}
                    onChange={(e) => setFilterRecurrence(e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="one_time">One-time</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </label>
                <div className="scheduling-classes-filter-row">
                  <label className="scheduling-classes-filter-label">
                    From
                    <input
                      type="date"
                      className="scheduling-classes-filter-input"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                    />
                  </label>
                  <label className="scheduling-classes-filter-label">
                    To
                    <input
                      type="date"
                      className="scheduling-classes-filter-input"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                    />
                  </label>
                </div>
                <div className="scheduling-classes-filter-row">
                  <label className="scheduling-classes-filter-label">
                    Min duration (min)
                    <input
                      type="number"
                      min="0"
                      className="scheduling-classes-filter-input"
                      placeholder="e.g. 30"
                      value={filterDurationMin}
                      onChange={(e) => setFilterDurationMin(e.target.value)}
                    />
                  </label>
                  <label className="scheduling-classes-filter-label">
                    Max duration (min)
                    <input
                      type="number"
                      min="0"
                      className="scheduling-classes-filter-input"
                      placeholder="e.g. 90"
                      value={filterDurationMax}
                      onChange={(e) => setFilterDurationMax(e.target.value)}
                    />
                  </label>
                </div>
                <footer className="scheduling-classes-filter-footer">
                  <button
                    type="button"
                    data-button-type="tertiary"
                    onClick={clearFilters}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    data-button-type="primary"
                    onClick={() => setFilterOpen(false)}
                  >
                    Done
                  </button>
                </footer>
              </div>
            )}
          </div>
        </nav>

        {loading && <CardListSkeleton theme={theme} />}
        {!loading && loadError && (
          <p className="scheduling-classes-error" role="alert">
            {loadError}
          </p>
        )}
        {!loading && !loadError && classes.length === 0 && (
          <SchedulingEmptyState
            iconSrc="/img/new-icons/classes-icon.svg"
            title="No classes yet"
            description="Create your first class to start scheduling members."
            action={
              <button
                type="button"
                data-button-type="primary"
                onClick={() => setCreateClassModalOpen(true)}
              >
                + Create Class
              </button>
            }
          />
        )}
        {!loading &&
          !loadError &&
          classes.length > 0 &&
          activeFilter === "expired" &&
          classes.filter((c) => c.isExpired).length === 0 && (
            <SchedulingEmptyState
              className="scheduling-empty-state--full"
              iconSrc="/img/new-icons/classes-icon.svg"
              title="No expired classes"
              description="Expired classes will appear here once they finish."
            />
          )}
        <ul className="scheduling-classes-list">
          {(() => {
            const base =
              activeFilter === "expired"
                ? classes.filter((c) => c.isExpired)
                : classes.filter((c) => !c.isExpired && c.status === "active");
            const q = filterQuery.trim().toLowerCase();
            const fromMs = filterDateFrom
              ? new Date(filterDateFrom).getTime()
              : null;
            const toMs = filterDateTo
              ? new Date(filterDateTo).getTime() + 86400000 - 1
              : null;
            const minDur = filterDurationMin ? Number(filterDurationMin) : null;
            const maxDur = filterDurationMax ? Number(filterDurationMax) : null;
            return base.filter((c) => {
              if (
                q &&
                !String(c.title || "")
                  .toLowerCase()
                  .includes(q)
              ) {
                return false;
              }
              if (filterRecurrence) {
                const r = c.raw?.recurrence_type ?? "";
                if (r !== filterRecurrence) return false;
              }
              if (fromMs != null || toMs != null) {
                const startStr =
                  c.raw?.recurrence_start_date ?? c.raw?.start_date ?? null;
                const endStr =
                  c.raw?.recurrence_end_date ?? c.raw?.end_date ?? null;
                const startMs = startStr ? new Date(startStr).getTime() : null;
                const endMs = endStr ? new Date(endStr).getTime() : startMs;
                if (fromMs != null && endMs != null && endMs < fromMs) {
                  return false;
                }
                if (toMs != null && startMs != null && startMs > toMs) {
                  return false;
                }
              }
              if (minDur != null || maxDur != null) {
                const dur = Number(c.raw?.duration_minutes ?? 0);
                if (minDur != null && dur < minDur) return false;
                if (maxDur != null && dur > maxDur) return false;
              }
              return true;
            });
          })().map((cls) => (
            <li key={cls.id} className="scheduling-class-card">
              <div className="scheduling-class-card-checkbox">
                <input
                  type="checkbox"
                  id={`class-${cls.id}`}
                  aria-label={`Select ${cls.title}`}
                  disabled={!canManageClass(cls)}
                />
              </div>
              <div className="scheduling-class-card-thumb">
                <img
                  src={cls.thumbnail}
                  alt=""
                  className={
                    cls.thumbnailIsPhoto
                      ? "scheduling-class-thumb-photo"
                      : "scheduling-class-thumb-icon"
                  }
                />
              </div>
              <div className="scheduling-class-card-main">
                <h3 className="scheduling-class-card-title">{cls.title}</h3>
                <p className="scheduling-class-card-desc">{cls.description}</p>
                <div className="scheduling-class-card-info">
                  <div className="scheduling-class-card-details">
                    <span title="Days">
                      <img src="/img/new-icons/calendar-ico.svg" alt="" />
                      {cls.days}
                    </span>
                    <span title="Duration">
                      <img
                        src="/img/new-icons/vuesax-linear-timer-pause.svg"
                        alt=""
                      />
                      {cls.duration}
                    </span>
                    <span title="Location">
                      <img src="/img/new-icons/push-pin.svg" alt="" />
                      {cls.location}
                    </span>
                    <span title="Time">
                      <img
                        src="/img/new-icons/vuesax-linear-clock.svg"
                        alt=""
                      />
                      {cls.time}
                    </span>
                    <span title="Recurrence">
                      <img
                        src="/img/new-icons/vuesax-linear-refresh-2.svg"
                        alt=""
                      />
                      {cls.recurrence}
                    </span>
                    <span title="Capacity">
                      <img
                        src="/img/new-icons/people-circle-outline.svg"
                        alt=""
                      />
                      {cls.capacity}
                    </span>
                  </div>
                  {cls.coaches.length > 0 && (
                    <div className="scheduling-class-card-coaches">
                      <span className="scheduling-class-card-coaches-label">
                        Coaches:
                      </span>
                      <span className="scheduling-class-card-avatar-stack scheduling-class-card-avatar-stack--hoverable">
                        {cls.coaches.slice(0, 3).map((c) => (
                          <span
                            key={c.id}
                            className="scheduling-class-card-avatar"
                          >
                            <img src={c.avatarUrl} alt="" />
                          </span>
                        ))}
                        {cls.coachesExtra != null && (
                          <span className="scheduling-class-card-avatar-extra-pill">
                            +{cls.coachesExtra}
                          </span>
                        )}
                        <span className="scheduling-class-card-coaches-popover">
                          {cls.coaches.map((c) => (
                            <span
                              key={c.id}
                              className="scheduling-class-card-coaches-popover-row"
                            >
                              <img src={c.avatarUrl} alt="" />
                              <span>{c.name || "Coach"}</span>
                            </span>
                          ))}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="scheduling-class-card-actions">
                {canManageClass(cls) && (
                  <button
                    type="button"
                    className="scheduling-class-card-edit-btn"
                    onClick={() => {
                      setModalInitialScrollSection(null);
                      setEditingClass(cls.raw);
                      setCreateClassModalOpen(true);
                    }}
                  >
                    <img src="/img/new-icons/edit-pencil.svg" alt="" />
                    Edit
                  </button>
                )}
                <div className="scheduling-class-card-menu-wrapper">
                  <button
                    type="button"
                    className="scheduling-class-card-menu-btn"
                    aria-expanded={menuOpenId === cls.id}
                    aria-haspopup="true"
                    onClick={() =>
                      setMenuOpenId(menuOpenId === cls.id ? null : cls.id)
                    }
                  >
                    <img
                      src="/img/new-icons/more-square.svg"
                      alt="More actions"
                    />
                  </button>
                  {menuOpenId === cls.id && (
                    <>
                      <div
                        className="scheduling-class-card-menu-backdrop"
                        onClick={() => setMenuOpenId(null)}
                        aria-hidden="true"
                      />
                      <ul className="scheduling-class-card-menu">
                        <li>
                          <a
                            href={buildSchedulingCalendarPreviewUrl(
                              "class",
                              cls.id
                            )}
                            onClick={() => setMenuOpenId(null)}
                          >
                            <img
                              src="/img/new-icons/vuesax-linear-play.svg"
                              alt=""
                            />
                            Preview in Calendar
                          </a>
                        </li>
                        <li>
                          <button
                            type="button"
                            onClick={() => duplicateClass(cls)}
                          >
                            <img src="/img/new-icons/duplicate.svg" alt="" />
                            Duplicate
                          </button>
                        </li>
                        {canManageClass(cls) && (
                          <li>
                            <button
                              type="button"
                              onClick={() => {
                                setMenuOpenId(null);
                                setBlockTimePreset({
                                  scheduleKind: BLOCK_TIME_SCHEDULE_CLASSES,
                                  bookableId: String(cls.id),
                                  scrollSection: BLOCK_TIME_SCROLL_SELECT_DATE,
                                });
                                setBlockTimeModalOpen(true);
                              }}
                            >
                              <img
                                src="/img/new-icons/close-circle-outline-white.svg"
                                alt=""
                              />
                              Block Time
                            </button>
                          </li>
                        )}
                        {showPermissionsAction(cls.raw) &&
                          canManageClass(cls) && (
                            <li>
                              <button
                                type="button"
                                onClick={() => {
                                  setMenuOpenId(null);
                                  setModalInitialScrollSection(
                                    "access_permissions"
                                  );
                                  setEditingClass(cls.raw);
                                  setCreateClassModalOpen(true);
                                }}
                              >
                                <img src="/img/new-icons/lock.svg" alt="" />
                                Permissions
                              </button>
                            </li>
                          )}
                        {cls.hasHistory && (
                          <li>
                            <button type="button">
                              <img
                                src="/img/new-icons/history-icon.svg"
                                alt=""
                              />
                              View Class History
                            </button>
                          </li>
                        )}
                        {canManageClass(cls) && (
                          <li>
                            <button
                              type="button"
                              className="scheduling-class-card-menu-delete"
                              onClick={() => deleteClass(cls)}
                            >
                              <img src="/img/new-icons/red-trash.svg" alt="" />
                              Delete
                            </button>
                          </li>
                        )}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <CreateBlockTimeModal
        key={
          blockTimeModalOpen && blockTimePreset
            ? `bt-${blockTimePreset.scheduleKind}-${blockTimePreset.bookableId}`
            : "block-time-closed"
        }
        isOpen={blockTimeModalOpen}
        listsLoading={loading}
        classes={classes
          .filter(canManageClass)
          .map((c) => c.raw)
          .filter(Boolean)}
        ptSessions={[]}
        facilities={[]}
        showFacilities={hasNonPullScheduling}
        initialScheduleKind={blockTimePreset?.scheduleKind ?? null}
        initialPreselectedBookableId={blockTimePreset?.bookableId ?? null}
        initialScrollSection={blockTimePreset?.scrollSection ?? null}
        onClose={() => {
          setBlockTimeModalOpen(false);
          setBlockTimePreset(null);
        }}
        onConfirm={async (payloads) => {
          await submitBlockTimePayloads(payloads);
          fetchClasses();
        }}
      />

      <CreateClassModal
        isOpen={createClassModalOpen}
        initialRecord={editingClass}
        initialScrollSection={modalInitialScrollSection}
        showAccessPermissions={hasNonPullScheduling}
        hasPressScheduling={hasPressScheduling}
        hasPullScheduling={hasPullScheduling}
        canPushUser={canPushUser}
        canPullUser={canPullUser}
        onClose={() => {
          setCreateClassModalOpen(false);
          setEditingClass(null);
          setModalInitialScrollSection(null);
        }}
        onConfirm={async (payload, fileOptions = {}) => {
          const id = editingClass?._id ?? editingClass?.id;
          const url = id
            ? `/actions/scheduling/classes/${encodeURIComponent(String(id))}`
            : "/actions/scheduling/classes";
          const method = id ? "PUT" : "POST";
          const files = fileOptions.thumbnailFile
            ? { thumbnail: fileOptions.thumbnailFile }
            : null;
          const res = await fetchSchedulingBookable(
            url,
            method,
            payload,
            files
          );
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(
              data.error ||
                (id ? "Failed to update class" : "Failed to create class")
            );
          }
          fetchClasses();
        }}
      />
    </div>
  );
};

export default SchedulingClasses;
